import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Legt das Gratis-Geschenk automatisch in den Warenkorb, sobald die Schwelle erreicht ist,
 * und entfernt es wieder, wenn der Warenkorb darunter fällt.
 * Ersetzt die Geschenk-Logik von UpCart. Konfiguration kommt aus data-Attributen (Theme-Einstellungen).
 */
class CartRewardsController extends HTMLElement {
  #busy = false;
  #timer = null;
  static SECTION = 'cart-drawer-section';

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#schedule();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    clearTimeout(this.#timer);
  }

  #onCartUpdate = (event) => {
    if (event.target === this) return;
    // Erst abgleichen, wenn die auslösende Änderung abgeschlossen ist
    const settle = event.promise ? event.promise.catch(() => {}) : Promise.resolve();
    settle.then(() => this.#schedule());
  };

  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#sync(), 30);
  }

  get #routes() {
    const r = window.Theme?.routes || {};
    return {
      cart: r.cart_url ? `${r.cart_url}.js` : '/cart.js',
      add: r.cart_add_url || '/cart/add.js',
      change: r.cart_change_url || '/cart/change.js',
      update: r.cart_update_url || '/cart/update.js',
    };
  }

  async #fetchCart() {
    const res = await fetch(this.#routes.cart, { credentials: 'same-origin', cache: 'no-store' });
    return res.json();
  }

  async #post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${url} → ${res.status}`);
    return res.json();
  }

  async #sync() {
    if (this.#busy) return;
    const giftVariant = Number(this.dataset.giftVariant);
    const target = Number(this.dataset.target);
    if (!giftVariant || !target || this.dataset.enabled !== 'true') return;
    const basis = this.dataset.basis || 'amount';

    this.#busy = true;
    try {
      const cart = await this.#fetchCart();
      const giftLines = [];
      let candles = 0;
      for (const item of cart.items) {
        if (item.properties && item.properties._gift) giftLines.push(item);
        else if ((item.product_type || '') === 'Zubehör') continue;
        else candles += item.quantity;
      }
      const current = basis === 'count' ? candles : cart.items_subtotal_price;
      const qualifies = candles > 0 && current >= target;
      let changed = false;

      if (qualifies && giftLines.length === 0) {
        await this.#post(this.#routes.add, {
          items: [{ id: giftVariant, quantity: 1, properties: { _gift: this.dataset.giftLabel || 'Gratis' } }],
        });
        changed = true;
      } else if (!qualifies && giftLines.length > 0) {
        for (const line of giftLines) {
          await this.#post(this.#routes.change, { id: line.key, quantity: 0 });
        }
        changed = true;
      } else if (giftLines.length > 0 && (giftLines.length > 1 || giftLines[0].quantity > 1)) {
        for (const [index, line] of giftLines.entries()) {
          await this.#post(this.#routes.change, { id: line.key, quantity: index === 0 ? 1 : 0 });
        }
        changed = true;
      }

      if (changed) this.#announce();
    } catch (error) {
      console.warn('[cart-rewards] Geschenk-Abgleich fehlgeschlagen', error);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * Meldet die Änderung dem Theme über das Standard-Event, damit Drawer und Warenkorb-Icon
   * neu gezeichnet werden. Die Sektions-HTML kommt aus einem Update-Aufruf ohne Änderung.
   */
  #announce() {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );
    this.#post(this.#routes.update, {
      updates: {},
      sections: CartRewardsController.SECTION,
      sections_url: window.location.pathname,
    })
      .then((response) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
          detail: {
            sections: response.sections,
            items: response.items,
            itemCount: response.item_count,
            source: 'cart-rewards',
            didError: false,
          },
        });
      })
      .catch((error) => deferred.reject(error));
  }
}

if (!customElements.get('cart-rewards-controller')) {
  customElements.define('cart-rewards-controller', CartRewardsController);
}

/**
 * Handlungszeile: Drawer schließen und navigieren. Zeigt der Link auf die aktuelle Seite,
 * wird nur der Drawer geschlossen, damit der Klick nicht ins Leere läuft.
 */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-cart-next-step]') : null;
  if (!link) return;
  const drawer = link.closest('theme-drawer') || link.closest('cart-drawer-component');
  let samePage = false;
  try {
    const target = new URL(link.href, location.href);
    samePage = target.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '');
  } catch (e) { /* ungültige URL: normal navigieren */ }
  if (samePage) event.preventDefault();
  drawer?.close?.();
});

/**
 * Gestaffelte Füllung der Segmente, unabhängig davon, ob Horizon die Knoten ersetzt oder nur
 * Attribute aktualisiert: beim Wachsen füllt sich erst Segment 1, dann 2, dann 3. Beim Schrumpfen
 * leert sich zuerst das letzte betroffene Segment. Der letzte bekannte Stand wird pro Segment gemerkt.
 */
const SEGMENT_FULL_MS = 650; // Zeit für ein komplettes Segment, daraus ergibt sich eine konstante Geschwindigkeit
const SEGMENT_MIN_MS = 90;
const widthOf = (style) => {
  const m = /width:\s*([\d.]+)%/.exec(style || '');
  return m ? parseFloat(m[1]) : 0;
};
const segmentState = { widths: null };
let animationFrame = 0;

function animateTrack() {
  animationFrame = 0;
  const track = document.querySelector('#cart-drawer .cart-rewards__track, cart-drawer-component .cart-rewards__track');
  if (!track) return;
  const fills = Array.from(track.querySelectorAll('.cart-rewards__fill'));
  const targets = fills.map((f) => widthOf(f.getAttribute('style')));
  if (!segmentState.widths) {
    segmentState.widths = targets;
    return;
  }
  const changes = fills
    .map((el, index) => ({ el, index, from: segmentState.widths[index] ?? 0, to: targets[index] }))
    .filter((c) => Math.abs(c.to - c.from) > 0.05);
  if (!changes.length) return;
  segmentState.widths = targets;

  const growing = changes.some((c) => c.to > c.from);
  changes.sort((a, b) => (growing ? a.index - b.index : b.index - a.index));

  // Startwerte ohne Übergang setzen
  changes.forEach((c) => {
    c.el.style.transition = 'none';
    c.el.style.width = `${c.from}%`;
  });
  void track.offsetWidth;

  // Eine durchgehende Bewegung: Dauer je Segment nach Strecke, nahtlos aneinander gehängt.
  // Anlauf nur im ersten, Abbremsen nur im letzten Segment, dazwischen linear.
  let offset = 0;
  const last = changes.length - 1;
  changes.forEach((c, order) => {
    const distance = Math.abs(c.to - c.from) / 100;
    const duration = Math.max(SEGMENT_MIN_MS, Math.round(distance * SEGMENT_FULL_MS));
    let easing = 'linear';
    if (changes.length === 1) easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
    else if (order === 0) easing = 'cubic-bezier(0.4, 0, 1, 1)';
    else if (order === last) easing = 'cubic-bezier(0, 0, 0.2, 1)';
    c.el.style.transition = `width ${duration}ms ${easing} ${offset}ms`;
    c.el.style.width = `${c.to}%`;
    offset += duration;
  });
}

const trackObserver = new MutationObserver((mutations) => {
  const relevant = mutations.some((m) => {
    if (m.type === 'attributes') {
      return m.target instanceof HTMLElement && m.target.classList.contains('cart-rewards__fill');
    }
    return Array.from(m.addedNodes).some(
      (n) => n instanceof HTMLElement && (n.matches('.cart-rewards, .cart-rewards__track') || n.querySelector?.('.cart-rewards__track'))
    );
  });
  if (!relevant || animationFrame) return;
  animationFrame = requestAnimationFrame(animateTrack);
});
trackObserver.observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['style'],
});
animateTrack();
